import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { FinanceData } from '../lib/api/boards';

export interface EntityPill {
  id: string;
  name: string;
  type: string;
  channelId: string;
  channelName: string;
  currency?: string;
  feed?: {
    source: string[];
    origin: string[];
    assets: {
      type: 'image' | 'video' | 'mix' | 'none' | 'unknown';
      high: string[];
      low: string[];
    };
  };
}

export interface CurrencyConversion {
  currency: string;
  rate: number;
  manual: boolean;
}

export interface CurrencySettings {
  default: string;
  conversions: CurrencyConversion[];
}

export interface BoardChannel {
  /**
   * Card identity in the UI: the BoardChannel row ID. For pseudo-channels,
   * each row backs its own card even when several share the same underlying
   * real channel. Backend also exposes this as `boardChannelId` for clarity.
   */
  id: string;
  boardChannelId?: string;
  /** Underlying real channel ID (Channel.id). For pseudos, equals pseudoOfChannelId. */
  channelId?: string;
  pseudoOfChannelId?: string | null;
  sourceEntityId?: string | null;
  isPseudoChannel?: boolean;
  name: string;
  channelType: string;
  status: string;
  order: number;
  icon?: string;
  isEnabled?: boolean; // New property for active status link
  channelTypeConfig?: {
    name?: string;
    icon?: string;
    category?: string;
    pseudoChannelEnabled?: boolean;
  } | null;
}

export interface BoardEntity {
  id: string;
  externalId?: string;
  channelId: string;
  name: string;
  entityType: string;
  currency?: string;
  parentId?: string;
  isSelected: boolean;
  isEnabled?: boolean;
  traffic?: any;
  publishedAt?: string | null;
  feed?: {
    source: string[];
    origin: string[];
    assets: {
      type: 'image' | 'video' | 'mix' | 'none' | 'unknown';
      high: string[];
      low: string[];
    };
  };
}

export interface BoardPage {
  id: string;
  name: string;
  type?: string;
  order: number;
  chartConfig: any[];
}

export interface BoardJournal {
  id: string;
  name: string;
  type: 'system' | 'custom';
  isEnabled: boolean;
  order: number;
}

export interface BoardSpec {
  id: string;
  name: string;
  formula: string;
  value?: number;
  error?: string;
  type: 'REVENUE' | 'EXPENSE';
}

interface BoardState {
  // State from server
  boardId: string | null;
  channels: BoardChannel[];
  entities: BoardEntity[];
  currencySettings: CurrencySettings | null;
  dateRange: { startDate: Date | null; endDate: Date | null };
  finance: FinanceData | null; // Store finance data
  unfilteredFinance: FinanceData | null; // Store unfiltered finance data for modals/specs
  events: any[] | null; // Store events data
  pages: BoardPage[];
  journals: BoardJournal[];
  specs: BoardSpec[];
  activePageId: string | null;
  channelErrors: Record<string, { code: string | number; message: string }>;
  selectedOperationIds: string[];
  operationStatuses: Record<string, boolean>; // Map of operationId -> isActive status
  isContentModalOpen: boolean; // Sync with UI modal state for onboarding guard
  hasAITasks: boolean; // Track if there are any AI tasks

  selectedEntities: EntityPill[];
  usedCurrencies: string[];
  hasMultipleCurrencies: boolean;
  isDataLoading: boolean; // Track when data is being loaded/updated

  // Actions
  setBoardData: (data: {
    board: any;
    channels: any[];
    journals?: any[];
    specs?: BoardSpec[];
    selectedEntitiesForDisplay?: EntityPill[];
    finance?: FinanceData | null;
    unfilteredFinance?: FinanceData | null;
    events?: any[] | null;
    stats?: { currencies?: string[] };
    errors?: Array<{ channelId: string; code: string | number; message: string; }>;
    metadata?: any;
  }) => void;
  setFinanceData: (finance: FinanceData) => void;
  setEventsData: (events: any[]) => void;
  setSelectedEntities: (entities: EntityPill[]) => void;
  setSelectedOperationIds: (ids: string[]) => void;
  setCurrencySettings: (settings: CurrencySettings) => void;
  setDateRange: (range: { startDate: Date | null; endDate: Date | null }) => void;
  updateEntitySelection: (entityId: string, isSelected: boolean) => void;
  updateDerivedState: () => void;
  clearBoardState: () => void;
  setIsDataLoading: (isLoading: boolean) => void;
  setActivePage: (pageId: string) => void;
  updatePage: (pageId: string, updates: Partial<BoardPage>) => void;
  addPage: (page: BoardPage) => void;
  deletePage: (pageId: string) => void;
  reorderPages: (orderedPageIds: string[]) => void;
  setJournals: (journals: BoardJournal[]) => void;
  addJournal: (journal: BoardJournal) => void;
  updateJournal: (journalId: string, updates: Partial<BoardJournal>) => void;
  deleteJournal: (journalId: string) => void;
  setSpecs: (specs: BoardSpec[]) => void;
  mergeChannelErrors: (errors: Array<{ channelId: string; code: string | number; message: string }>) => void;
  setIsContentModalOpen: (isOpen: boolean) => void;
  setHasAITasks: (hasTasks: boolean) => void;
  updateChannelStatus: (channelId: string, status: string) => void;


  // Selectors
  getConversionRate: (currency: string) => number;

}

export const useBoardStore = create<BoardState>()(
  devtools(
    (set, get) => ({
      // Initial state
      boardId: null,
      channels: [],
      entities: [],
      selectedEntities: [],
      channelErrors: {},
      currencySettings: null,
      dateRange: { startDate: null, endDate: null },
      finance: null,
      unfilteredFinance: null,
      events: null,
      usedCurrencies: [],
      hasMultipleCurrencies: false,
      isDataLoading: false,
      activePageId: null,
      pages: [],
      journals: [],
      specs: [],
      selectedOperationIds: [],
      operationStatuses: {},
      isContentModalOpen: false,
      hasAITasks: false,


      // Actions
      setBoardData: (data) => {
        // Preserve any optimistic pseudo-channel rows that the user just
        // added but the canonical payload hasn't observed yet (e.g. a WS-
        // triggered refetch racing against an in-flight POST /pseudo-channels
        // request). Once the canonical channels include the same source
        // entity, the optimistic placeholder is dropped naturally below.
        const existingOptimistic = (useBoardStore.getState().channels || []).filter(
          (c) => typeof c.id === 'string' && c.id.startsWith('optimistic-pseudo-'),
        );

        // Use pre-compiled selectedEntitiesForDisplay from backend
        const selectedEntities: EntityPill[] = data.selectedEntitiesForDisplay || [];

        // Flatten hierarchical entities from channels into flat array for tree display
        const flatEntities: BoardEntity[] = [];

        // Create a set of selected IDs from the display list for fallback
        const selectedIdsFromDisplay = new Set(selectedEntities.map(e => e.id));

        data.channels.forEach((channel: any) => {
          const flattenEntity = (entity: any, parentId?: string, parentIsSelected: boolean = false) => {
            // Determine if this entity is selected:
            // 1. Explicitly set in entity data
            // 2. In the selectedEntitiesForDisplay list
            // 3. Parent is selected (cascade)
            const isSelected = entity.isSelected || selectedIdsFromDisplay.has(entity.id) || parentIsSelected;

            // Safely parse feed structure if available
            type AssetsStructure = { type: 'image' | 'video' | 'mix' | 'none' | 'unknown'; high: string[]; low: string[]; };
            let parsedFeed: { source: string[]; origin: string[]; assets: AssetsStructure } = { source: [], origin: [], assets: { type: 'none', high: [], low: [] } };

            if (entity.feed && typeof entity.feed === 'object') {
              parsedFeed = {
                source: Array.isArray(entity.feed.source) ? entity.feed.source : [],
                origin: Array.isArray(entity.feed.origin) ? entity.feed.origin : [],
                assets: entity.feed.assets || { type: 'none', high: [], low: [] }
              };
            } else {
              // Fallback for older data or different API formats
              if (entity.source) {
                if (Array.isArray(entity.source)) {
                  parsedFeed.source = entity.source;
                } else if (typeof entity.source === 'string') {
                  try {
                    const parsed = JSON.parse(entity.source);
                    parsedFeed.source = Array.isArray(parsed) ? parsed : [parsed];
                  } catch (e) {
                    parsedFeed.source = [entity.source];
                  }
                }
              }

              if (entity.origin) {
                if (Array.isArray(entity.origin)) {
                  parsedFeed.origin = entity.origin;
                } else if (typeof entity.origin === 'string') {
                  try {
                    const parsed = JSON.parse(entity.origin);
                    parsedFeed.origin = Array.isArray(parsed) ? parsed : [parsed];
                  } catch (e) {
                    parsedFeed.origin = [entity.origin];
                  }
                }
              }

              // Extract assets fallback
              const emptyAssets: AssetsStructure = { type: 'none', high: [], low: [] };
              if (entity.assets) {
                if (Array.isArray(entity.assets)) {
                  parsedFeed.assets = { ...emptyAssets, type: 'image', high: entity.assets };
                } else {
                  parsedFeed.assets = entity.assets;
                }
              } else if (entity.metadata) {
                try {
                  const meta = typeof entity.metadata === 'string' ? JSON.parse(entity.metadata) : (entity.metadata || {});
                  if (Array.isArray(meta.assets)) {
                    parsedFeed.assets = { ...emptyAssets, type: 'image', high: meta.assets };
                  } else if (meta.assets?.type) {
                    parsedFeed.assets = meta.assets;
                  }
                } catch (e) { }
              }
            }

            flatEntities.push({
              id: entity.id,
              externalId: entity.externalId,
              channelId: channel.id,
              name: entity.name,
              entityType: entity.type || entity.entityType,
              currency: entity.currency,
              parentId: parentId || entity.parentId || null,
              isSelected: isSelected,
              isEnabled: entity.isEnabled,
              traffic: entity.traffic,
              publishedAt: entity.publishedAt,
              feed: parsedFeed
            });

            if (entity.children && entity.children.length > 0) {
              entity.children.forEach((child: any) => flattenEntity(child, entity.id, isSelected));
            }
          };

          if (channel.entities && channel.entities.length > 0) {
            channel.entities.forEach((entity: any) => flattenEntity(entity));
          }
        });

        // Extract currency settings from board data using new schema
        let currencySettings: CurrencySettings | null = null;
        if (data.board && data.board.currency) {
          currencySettings = {
            default: data.board.currency.default || '',
            conversions: data.board.currency.conversions || []
          };
        }

        // Parse pages
        const pages: BoardPage[] = data.board?.pages || [];
        // Parse journals — apply the same merge pattern used for specs below so
        // an optimistic addJournal (e.g. just-created journal being linked) is
        // not wiped by a slightly-stale refetch that returns the old list.
        const incomingJournals: BoardJournal[] = data.journals || data.board?.journals || [];
        const currentJournals = get().journals;
        const journals: BoardJournal[] = [...incomingJournals];
        currentJournals.forEach((existing) => {
          if (!incomingJournals.some((ij) => ij.id === existing.id)) {
            journals.push(existing);
          }
        });
        // Parse specs - merge with existing to preserve calculated values
        const currentSpecs = get().specs;
        const incomingSpecs = data.specs || data.board?.specs || [];
        
        console.log('[BoardStore] setBoardData incoming specs count:', incomingSpecs.length);
        
        const mappedSpecs: BoardSpec[] = incomingSpecs.map((newSpec: any) => {
          const existing = currentSpecs.find(s => s.id === newSpec.id);
          return {
            ...newSpec,
            value: newSpec.value !== undefined ? newSpec.value : existing?.value
          };
        });

        // MERGE LOGIC: Keep specs that are in current state but missing from response
        // This prevents optimistic updates from being wiped by slightly stale refetches
        const specs = [...mappedSpecs];
        currentSpecs.forEach(existing => {
          if (!incomingSpecs.some((is: any) => is.id === existing.id)) {
            // Check if it's a recently added spec (optimistic)
            // Or just keep it for now - better to have a ghost than a missing spec while it's being saved
            specs.push(existing);
          }
        });

        if (specs.length !== currentSpecs.length || incomingSpecs.length !== currentSpecs.length) {
            console.log(`[BoardStore] Spec count: current=${currentSpecs.length}, incoming=${incomingSpecs.length}, final=${specs.length}`);
        }

        // Parse selected operation IDs and their statuses
        const selectedOperationsData = data.board?.selectedOperations || [];
        const selectedOperationIds: string[] = selectedOperationsData.map((op: any) => op.operationId);
        const operationStatuses: Record<string, boolean> = {};
        selectedOperationsData.forEach((op: any) => {
          operationStatuses[op.operationId] = op.isActive !== false; // Default to true if not specified
        });

        // Determine active page
        let activePageId = get().activePageId;
        const boardId = data.board?.id;
        const storageKey = boardId ? `dashbal_board_${boardId}_activePage` : null;

        // Helper to check if a page ID exists in the new pages list
        const isValidPage = (id: string | null) => id && pages.some(p => p.id === id);

        // 1. Try to keep current active page if valid
        if (!isValidPage(activePageId)) {
          activePageId = null;

          // 2. Try to restore from localStorage
          if (storageKey) {
            const storedId = localStorage.getItem(storageKey);
            if (isValidPage(storedId)) {
              activePageId = storedId;
            }
          }

          // 3. Fallback to first page if available
          if (!activePageId && pages.length > 0) {
            activePageId = pages[0].id;
          }
        }

        // Process errors into a map
        const channelErrors: Record<string, { code: string | number; message: string }> = {};
        if (data.errors && Array.isArray(data.errors)) {
          data.errors.forEach(err => {
            if (err.channelId) {
              channelErrors[err.channelId] = {
                code: err.code,
                message: err.message
              };
            }
          });
        }

        const incomingChannels = data.channels.map((channel: any) => ({
          id: channel.id,
          boardChannelId: channel.boardChannelId ?? channel.id,
          channelId: channel.channelId ?? channel.id,
          pseudoOfChannelId: channel.pseudoOfChannelId ?? null,
          sourceEntityId: channel.sourceEntityId ?? null,
          isPseudoChannel: Boolean(channel.isPseudoChannel),
          name: channel.name,
          channelType: channel.channelType,
          status: channel.status,
          order: channel.order,
          icon: channel.icon,
          channelTypeConfig: channel.channelTypeConfig,
          isEnabled: channel.isEnabled,
        }));
        const incomingSourceIds = new Set(
          incomingChannels
            .filter((c) => c.isPseudoChannel && c.sourceEntityId)
            .map((c) => c.sourceEntityId as string),
        );
        const survivingOptimistic = existingOptimistic.filter(
          (c) =>
            c.sourceEntityId == null ||
            !incomingSourceIds.has(c.sourceEntityId),
        );
        const channelsWithOptimistic = [
          ...incomingChannels,
          ...survivingOptimistic,
        ];

        set((state) => ({
          boardId: boardId,
          channels: channelsWithOptimistic,
          entities: flatEntities,
          selectedEntities: selectedEntities,
          currencySettings,
          // Update finance/events if provided (including null), preserve existing state if undefined
          finance: data.finance !== undefined ? data.finance : state.finance,
          unfilteredFinance: data.unfilteredFinance !== undefined
            ? data.unfilteredFinance
            : (data.finance?.unfilteredFinance !== undefined
              ? data.finance.unfilteredFinance
              : state.unfilteredFinance),
          events: data.events !== undefined ? data.events : state.events,
          pages: pages,
          journals: journals,
          specs: specs,
          selectedOperationIds: selectedOperationIds,
          operationStatuses: operationStatuses,
          activePageId: activePageId,
          channelErrors, // Update errors state


        }));

        // Update currencies - prefer backend stats.currencies (includes operations), fallback to entity currencies
        let allCurrencies: Set<string>;
        if (data.stats?.currencies && Array.isArray(data.stats.currencies)) {
          // Use backend-calculated currencies (includes both entity and operation currencies)
          allCurrencies = new Set(data.stats.currencies);
        } else {
          // Fallback: calculate from entities only
          allCurrencies = new Set(
            flatEntities
              .map(e => e.currency)
              .filter(Boolean) as string[]
          );
        }

        set({
          usedCurrencies: Array.from(allCurrencies),
          hasMultipleCurrencies: allCurrencies.size > 1
        });
      },

      setFinanceData: (financeData) => {
        // Extract unfilteredFinance if it was included in the finance response (backend pattern)
        const finance = financeData;
        const unfilteredFinance = financeData?.unfilteredFinance;

        set({
          finance,
          unfilteredFinance: unfilteredFinance !== undefined ? unfilteredFinance : get().unfilteredFinance
        });
        if (finance?.specs) {
          const currentSpecs = get().specs;
          const incomingSpecsMap = new Map<string, any>(finance.specs.map((s: any) => [s.id, s]));

          // Merge: Update 'value' and 'error' from finance results, preserve local definitions (name, formula)
          const mergedSpecs = currentSpecs.map(existing => {
            const incoming = incomingSpecsMap.get(existing.id);
            if (incoming) {
              return {
                ...existing,
                value: incoming.value,
                error: incoming.error || null
              };
            }
            return existing;
          });

          // Add brand new specs that weren't in current state
          finance.specs.forEach((incoming: any) => {
            if (!currentSpecs.some(s => s.id === incoming.id)) {
              mergedSpecs.push(incoming);
            }
          });

          set({ specs: mergedSpecs });
        }
      },

      setEventsData: (events) => {
        set({ events });
      },

      setSelectedEntities: (entities) => {
        set({ selectedEntities: entities });
        get().updateDerivedState();
      },

      setSelectedOperationIds: (ids) => {
        set({ selectedOperationIds: ids });
      },

      updateEntitySelection: (entityId, isSelected) => {
        set(state => ({
          entities: state.entities.map(e =>
            e.id === entityId ? { ...e, isSelected } : e
          )
        }));
        get().updateDerivedState();
      },

      setCurrencySettings: (settings) => {
        set({ currencySettings: settings });
        get().updateDerivedState();
      },

      setDateRange: (range) => set({ dateRange: range }),

      updateDerivedState: () => {
        const state = get();
        const { entities, channels } = state;

        // Compute selectedEntities from entities immediately for UI responsiveness
        const selectedEntityIds = new Set(entities.filter(e => e.isSelected).map(e => e.id));

        // Filter out entities that have a selected parent (only show top-level in pills)
        const topLevelSelectedEntities = entities.filter(entity => {
          if (!entity.isSelected) return false;

          // Check if any parent is selected
          let currentParentId: string | undefined = entity.parentId;
          while (currentParentId) {
            if (selectedEntityIds.has(currentParentId)) {
              return false; // Parent is selected, so exclude this entity from pills
            }
            const parent = entities.find(e => e.id === currentParentId);
            currentParentId = parent?.parentId;
          }

          return true; // No selected parent, include this entity in pills
        });

        // Map to EntityPill format
        const selectedEntities: EntityPill[] = topLevelSelectedEntities.map(entity => {
          const channel = channels.find(c => c.id === entity.channelId);
          return {
            id: entity.id,
            name: entity.name,
            type: entity.entityType,
            channelId: entity.channelId,
            channelName: channel?.name || 'Unknown Channel',
            currency: entity.currency,
            feed: entity.feed
          };
        });

        set({
          selectedEntities, // Update selectedEntities immediately
        });
      },

      clearBoardState: () => set({
        boardId: null,
        channels: [],
        entities: [],
        selectedEntities: [],
        currencySettings: null,
        dateRange: { startDate: null, endDate: null },
        finance: null,
        unfilteredFinance: null,
        events: null,
        usedCurrencies: [],
        hasMultipleCurrencies: false,
        isDataLoading: false,
        pages: [],
        journals: [],
        specs: [],
        activePageId: null,
        channelErrors: {},
        operationStatuses: {},


      }),

      setIsDataLoading: (isLoading: boolean) => set({ isDataLoading: isLoading }),

      setActivePage: (pageId) => {
        set({ activePageId: pageId });
        const { boardId } = get();
        if (boardId) {
          localStorage.setItem(`dashbal_board_${boardId}_activePage`, pageId);
        }
      },

      updatePage: (pageId, updates) => set((state) => ({
        pages: state.pages.map(p => p.id === pageId ? { ...p, ...updates } : p)
      })),

      addPage: (page) => set((state) => ({
        pages: [...state.pages, page].sort((a, b) => a.order - b.order),
        activePageId: page.id // Switch to new page
      })),

      deletePage: (pageId) => set((state) => {
        const newPages = state.pages.filter(p => p.id !== pageId);
        // If we deleted the active page, switch to another one
        let newActiveId = state.activePageId;
        if (state.activePageId === pageId) {
          newActiveId = newPages.length > 0 ? newPages[0].id : null;
        }
        return {
          pages: newPages,
          activePageId: newActiveId
        };
      }),

      reorderPages: (orderedPageIds) => set((state) => {
        // Create a map of current pages by ID
        const pageMap = new Map(state.pages.map(p => [p.id, p]));
        // Reorder pages based on the provided order and update their order property
        const reorderedPages = orderedPageIds
          .map((id, index) => {
            const page = pageMap.get(id);
            if (page) {
              return { ...page, order: index };
            }
            return null;
          })
          .filter((p): p is BoardPage => p !== null);
        return { pages: reorderedPages };
      }),

      setJournals: (journals) => set({ journals }),

      addJournal: (journal) => set((state) => ({
        journals: [...state.journals, journal].sort((a, b) => a.order - b.order)
      })),

      updateJournal: (journalId, updates) => set((state) => ({
        journals: state.journals.map(s => s.id === journalId ? { ...s, ...updates } : s)
      })),

      deleteJournal: (journalId) => set((state) => ({
        journals: state.journals.filter(s => s.id !== journalId)
      })),

      setSpecs: (specs) => {
        console.log('[BoardStore] setSpecs called with count:', specs.length);
        set({ specs });
      },

      mergeChannelErrors: (errors: Array<{ channelId: string; code: string | number; message: string }>) => set((state) => {
        const newErrors = { ...state.channelErrors };
        errors.forEach(err => {
          if (err.channelId) {
            newErrors[err.channelId] = {
              code: err.code,
              message: err.message
            };
          }
        });
        return { channelErrors: newErrors };
      }),
      
      setIsContentModalOpen: (isOpen) => set({ isContentModalOpen: isOpen }),

      setHasAITasks: (hasTasks: boolean) => set({ hasAITasks: hasTasks }),
      
      updateChannelStatus: (channelId: string, status: string) => set((state) => ({
        channels: state.channels.map(ch => 
          String(ch.id) === String(channelId) ? { ...ch, status } : ch
        )
      })),

      // Selectors
      getConversionRate: (currency: string) => {
        const { currencySettings } = get();
        if (!currencySettings || !currency) return 1;
        if (currency === currencySettings.default) return 1;

        const conversion = currencySettings.conversions.find(
          c => c.currency === currency
        );
        return conversion?.rate || 1;
      },

    }),
    { name: 'BoardStore' }
  )
);
